import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';

import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { workoutPlanService, userProfileService } from '../../services/supabaseService';
import AssignedWorkoutCard from './components/AssignedWorkoutCard';
import ClientListCard from './components/ClientListCard';

const WorkoutManagement = () => {
  const { user, userProfile } = useAuth();
  const [assignedWorkouts, setAssignedWorkouts] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('workouts');

  const isTrainer = userProfile?.role === 'personal_trainer';

  useEffect(() => {
    loadData();
  }, [user, userProfile]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const { data: workouts } = await workoutPlanService?.getAssignedWorkouts(
        user?.id,
        userProfile?.role
      );
      setAssignedWorkouts(workouts || []);

      if (isTrainer) {
        const { data: clientsList } = await userProfileService?.getTrainerClients(user?.id);
        setClients(clientsList || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (workoutId, newStatus) => {
    await workoutPlanService?.updateWorkoutStatus(workoutId, newStatus);
    loadData();
  };

  const upcomingWorkouts = assignedWorkouts?.filter(
    w => w?.status === 'scheduled' || w?.status === 'in_progress'
  );
  const completedWorkouts = assignedWorkouts?.filter(w => w?.status === 'completed');

  return (
    <>
      <Helmet>
        <title>{isTrainer ? 'Client Management' : 'My Workouts'} - FormForge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Inter Tight', sans-serif", color: '#E8EAED' }}>
              {isTrainer ? 'Client Management' : 'My Workouts'}
            </h1>
            <p className="mt-2" style={{ color: '#6B7280' }}>
              {isTrainer ? 'Manage your clients and their assigned workouts' : 'View and complete your assigned workouts'}
            </p>
          </div>

          {isTrainer && (
            <div className="flex gap-3 mb-6">
              {[
                { id: 'workouts', icon: 'Dumbbell', label: 'Assigned Workouts' },
                { id: 'clients', icon: 'Users', label: 'My Clients' }
              ]?.map(tab => (
                <button key={tab?.id} onClick={() => setActiveTab(tab?.id)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all" style={activeTab === tab?.id ? { background: 'rgba(255,107,0,0.15)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' } : { background: 'rgba(255,255,255,0.04)', color: '#6B7280', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name={tab?.icon} size={18} />{tab?.label}
                </button>
              ))}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#FF6B00' }}></div>
            </div>
          ) : (
            <>
              {activeTab === 'workouts' && (
                <div className="space-y-8">
                  <div>
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ color: '#E8EAED' }}>
                      <Icon name="Calendar" size={20} style={{ color: '#00D4FF' }} />
                      Upcoming Workouts
                    </h2>
                    {upcomingWorkouts?.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {upcomingWorkouts?.map((workout) => (
                          <AssignedWorkoutCard key={workout?.id} workout={workout} isTrainer={isTrainer} onStatusUpdate={handleStatusUpdate} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <Icon name="Calendar" size={48} className="mx-auto mb-4" style={{ color: '#6B7280' }} />
                        <p style={{ color: '#6B7280' }}>No upcoming workouts</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ color: '#E8EAED' }}>
                      <Icon name="CheckCircle" size={20} style={{ color: '#AAFF00' }} />
                      Completed Workouts
                    </h2>
                    {completedWorkouts?.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {completedWorkouts?.slice(0, 6)?.map((workout) => (
                          <AssignedWorkoutCard key={workout?.id} workout={workout} isTrainer={isTrainer} onStatusUpdate={handleStatusUpdate} />
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <Icon name="CheckCircle" size={48} className="mx-auto mb-4" style={{ color: '#6B7280' }} />
                        <p style={{ color: '#6B7280' }}>No completed workouts yet</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'clients' && isTrainer && (
                <div>
                  {clients?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {clients?.map((assignment) => (
                        <ClientListCard key={assignment?.id} assignment={assignment} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-20 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                      <Icon name="Users" size={48} className="mx-auto mb-4" style={{ color: '#6B7280' }} />
                      <p style={{ color: '#6B7280' }}>No clients assigned yet</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default WorkoutManagement;